import { useState, useEffect } from "react";
import { Menu, X, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeToggle } from "./ThemeToggle";

const labelMap = { story: "Our Story", events: "Events", travel: "Getting There", gallery: "Gallery", guestbook: "Guestbook", suggestions: "Community Events" };

export const Navbar = ({ names, sections, order = [], guestbookOn, suggestionsOn }) => {
  const links = order
    .filter((id) => {
      if (!labelMap[id]) return false;
      if (id === "guestbook") return !!guestbookOn;
      if (id === "suggestions") return !!suggestionsOn;
      return sections?.[id] !== false;
    })
    .map((id) => ({ id, label: labelMap[id] }));
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  const textColor = scrolled ? "var(--text-secondary)" : "rgba(255,255,255,0.95)";
  const solidColor = scrolled ? "var(--burgundy)" : "#fff";

  return (
    <>
      <header
        className="fixed top-0 inset-x-0 z-40 transition-all duration-500 backdrop-blur-xl"
        style={scrolled
          ? { backgroundColor: "var(--nav-bg)", boxShadow: "0 6px 24px -14px rgba(0,0,0,0.35)" }
          : { backgroundColor: "rgba(0,0,0,0.12)" }}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 flex items-center justify-between h-16 sm:h-20">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2 font-serif text-lg sm:text-xl tracking-wide"
            data-testid="nav-logo"
            style={{ color: solidColor }}
          >
            <Heart className="w-4 h-4" style={{ color: "var(--gold)" }} />
            <span className="italic">{names}</span>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="text-sm tracking-wide transition-colors hover:opacity-70"
                style={{ color: textColor }}
              >
                {l.label}
              </button>
            ))}
            <ThemeToggle style={{ color: textColor }} />
            <button
              onClick={() => go("rsvp")}
              data-testid="nav-link-rsvp"
              className="rounded-full px-5 py-2 text-sm font-medium transition-all hover:scale-105"
              style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }}
            >
              RSVP
            </button>
          </nav>

          {/* Mobile menu button — clearly labelled pill */}
          <button
            className="md:hidden flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium backdrop-blur-md transition-all active:scale-95"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-toggle"
            aria-label="Open menu"
            style={scrolled
              ? { color: "var(--burgundy)", borderColor: "var(--border)", backgroundColor: "var(--card)" }
              : { color: "#fff", borderColor: "rgba(255,255,255,0.6)", backgroundColor: "rgba(255,255,255,0.12)" }}
          >
            <Menu className="w-5 h-5" /> Menu
          </button>
        </div>
      </header>

      {/* Mobile drawer — sibling of header so `fixed` maps to the viewport (header's backdrop-blur would otherwise clip it) */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 z-[60] flex flex-col"
            style={{ backgroundColor: "var(--ivory)" }}
            data-testid="nav-mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b shrink-0" style={{ borderColor: "var(--border)" }}>
              <span className="flex items-center gap-2 font-serif italic text-lg text-[var(--burgundy)]">
                <Heart className="w-4 h-4 text-[var(--gold)]" /> {names}
              </span>
              <button onClick={() => setOpen(false)} data-testid="nav-mobile-close" aria-label="Close menu" className="p-2 text-[var(--text-secondary)]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-b shrink-0" style={{ borderColor: "var(--border)" }}>
              <span className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Appearance</span>
              <ThemeToggle style={{ color: "var(--text-secondary)" }} />
            </div>

            <nav className="flex-1 overflow-y-auto px-6 py-4 flex flex-col">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.05 }}
                  onClick={() => go(l.id)}
                  className="text-left py-4 text-xl font-medium text-[var(--text-primary)] border-b border-[var(--border)]"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>

            <div className="p-6 border-t shrink-0" style={{ borderColor: "var(--border)" }}>
              <button
                onClick={() => go("rsvp")}
                className="w-full rounded-full py-3.5 text-sm font-medium shadow-lg"
                style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }}
                data-testid="nav-mobile-rsvp"
              >
                RSVP Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
